/**
 * AutoInterviewSettings — job-level "Auto-schedule interviews" control, rendered
 * as a compact single-row bar at the end of the Sourcing tab (below the method
 * cards).
 *
 * A global job setting (not tied to any one source): when on, ANY candidate whose
 * CV is scored at or above the threshold is automatically invited to an interview,
 * whatever way they were added (application link, manual upload, talent pool,
 * agent). Backed by the job's enableCvAutoInterview / cvInterviewThreshold fields.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarClock } from 'lucide-react';

import { JobOffer } from '../../models/job';
import {
  useToggleEnableCvAutoInterview,
  useUpdateCvInterviewThreshold,
} from '../../hooks/useJobOffers';
import { Slider } from '../ui/slider';

/** Brand-styled switch. */
const Switch: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
}> = ({ checked, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-purple/40 ${
      checked ? 'bg-purple' : 'bg-light-lila'
    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-light shadow-sm transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

interface AutoInterviewSettingsProps {
  job: JobOffer;
}

export const AutoInterviewSettings: React.FC<AutoInterviewSettingsProps> = ({ job }) => {
  const { t } = useTranslation('job');
  const [enabled, setEnabled] = useState(() => Boolean(job.enableCvAutoInterview));
  const [threshold, setThreshold] = useState(() => job.cvInterviewThreshold ?? 75);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const toggleAutoInterview = useToggleEnableCvAutoInterview(job.id);
  const updateThreshold = useUpdateCvInterviewThreshold(job.id);

  useEffect(() => {
    setEnabled(Boolean(job.enableCvAutoInterview));
    setThreshold(job.cvInterviewThreshold ?? 75);
  }, [job.enableCvAutoInterview, job.cvInterviewThreshold, job.id]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const handleToggle = useCallback(
    (next: boolean) => {
      toggleAutoInterview.mutate(next, {
        onSuccess: () => {
          setEnabled(next);
          toast.success(next ? t('settings.toast_enabled') : t('settings.toast_disabled'));
        },
        onError: (e: Error) => toast.error(t('settings.toast_failed', { error: e.message })),
      });
    },
    [toggleAutoInterview, t],
  );

  const handleThresholdChange = useCallback(
    (values: number[]) => {
      const next = values[0];
      setThreshold(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateThreshold.mutate(next, {
          onSuccess: () => toast.success(t('settings.toast_threshold_set', { threshold: next })),
          onError: (e: Error) => {
            toast.error(t('settings.toast_failed', { error: e.message }));
            setThreshold(job.cvInterviewThreshold ?? 75);
          },
        });
      }, 500);
    },
    [updateThreshold, job.cvInterviewThreshold, t],
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-xl border border-light-lila/60 bg-light px-4 py-3">
      <div className="flex min-w-0 items-start gap-2.5">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-dark-invirtus-soft text-dark-invirtus">
          <CalendarClock className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="font-body text-sm font-medium text-dark-invirtus">
            {t('settings.auto_interview_label')}
          </p>
          <p className="mt-0.5 font-body text-xs leading-relaxed text-dark-invirtus/55">
            {t('settings.auto_interview_desc')}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4">
        {enabled && (
          <div className="flex items-center gap-2.5">
            <span className="hidden font-body text-xs text-dark-invirtus/55 sm:inline">
              {t('settings.threshold_label')}
            </span>
            <Slider
              value={[threshold]}
              onValueChange={handleThresholdChange}
              min={0}
              max={100}
              step={1}
              disabled={updateThreshold.isPending}
              className="w-28 sm:w-40"
            />
            <span className="w-9 text-right font-body text-xs font-semibold tabular-nums text-dark-invirtus">
              {threshold}%
            </span>
          </div>
        )}
        <Switch
          checked={enabled}
          disabled={toggleAutoInterview.isPending}
          onChange={handleToggle}
          label={t('settings.auto_interview_label')}
        />
      </div>
    </div>
  );
};

export default AutoInterviewSettings;
